/**
 * ZELLE FIRST (ops #46). Zelle costs the business nothing, so the pay page offers it first, with a
 * bank account and then a card behind it, and never at a different price (CLAUDE.md binding rule 3):
 * the published price is the price however it is paid, and the nudge is words and ease alone.
 *
 * THE ONE STORED VALUE. The owner handed over the business's Zelle QR code from the Chase app on
 * 2026-09-18, and a Zelle code is a URL whose `data` is base64 JSON naming the recipient. That URL is
 * kept here, exactly as the code encodes it, and it is the whole of what is stored: the tag a payer
 * types and the name her own bank shows her before she sends are decoded from it by `ZELLE`, and
 * never typed a second time anywhere, so the day the tag or the name changes the owner hands over the
 * new code and this one string moves. `api/test/zelle.test.ts` decodes it, and fails the suite on
 * any other file in the repository that spells either word. It moves to a `settings` row the day ops
 * has a door that writes one (ops #21).
 *
 * The URL is also the code itself: a page renders the QR from this string at any size, so no image
 * is stored or served, and a phone that cannot scan its own screen gets the tag to paste and the URL
 * as a plain link. The code carries the recipient and nothing else, no amount and no memo, so both
 * are shown beside it.
 */

/** The owner's Zelle QR code, as the URL it encodes. Source: the Chase app, 2026-09-18. */
export const ZELLE_QR_URL =
  'https://enroll.zellepay.com/qr-codes/?data=eyJuYW1lIjoiRkFJVEhGVUxMWSBLRVBUIExMQyIsInRva2VuIjoiZmFpdGhmdWxseWtlcHQiLCJhY3Rpb24iOiJwYXltZW50In0=';

export interface ZelleCode {
  /** The Zelle tag a payer types, pastes or scans, on her bank app's Select Recipient screen. */
  readonly tag: string;
  /** The name her bank shows her before she sends, so she can check the recipient first. */
  readonly name: string;
  /** What the code asks a scanner to do. Only `payment` is a code a page may show. */
  readonly action: string;
}

/** Where a Zelle code points, and the only host whose code is read. */
const ZELLE_QR_HOST = 'enroll.zellepay.com';

/**
 * The `data` parameter as written, because `URLSearchParams` reads a plus sign as a space and a plus
 * is one of base64's own characters.
 */
const DATA = /(?:^|[?&])data=([^&#]*)/;

function refuse(why: string): never {
  throw new Error(`not a Zelle payment code: ${why}`);
}

/** A field of the code: a string with something in it and no white space around it. */
function wordIn(data: Record<string, unknown>, key: string): string {
  const value = data[key];
  if (typeof value !== 'string' || value.trim() === '' || value.trim() !== value) {
    refuse(`it names no ${key}`);
  }
  return value;
}

/**
 * Reads a Zelle QR code's URL. Throws, saying why, for anything that is not a Zelle payment code: a
 * page that showed a wrong code would send a client's money to whoever it named.
 */
export function decodeZelleQr(url: string): ZelleCode {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return refuse('it is not a URL');
  }
  if (parsed.protocol !== 'https:' || parsed.hostname !== ZELLE_QR_HOST) {
    refuse(`it is not an address on ${ZELLE_QR_HOST}`);
  }
  const raw = DATA.exec(parsed.search)?.[1];
  if (!raw) refuse('it carries no data');
  let data: unknown;
  try {
    // Standard or URL-safe base64, with or without its padding.
    const base64 = decodeURIComponent(raw).replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    const text = new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(
      Uint8Array.from(binary, (character) => character.charCodeAt(0)),
    );
    data = JSON.parse(text);
  } catch {
    return refuse('its data is not base64 JSON');
  }
  if (typeof data !== 'object' || data === null) refuse('its data is not an object');
  const fields = data as Record<string, unknown>;
  const tag = wordIn(fields, 'token');
  const name = wordIn(fields, 'name');
  const action = wordIn(fields, 'action');
  if (action !== 'payment') refuse(`it asks for ${action}, not a payment`);
  return { tag, name, action };
}

/** The recipient every screen, message and push reads: the code, and the two words decoded from it. */
export const ZELLE: { readonly qrUrl: string; readonly tag: string; readonly name: string } =
  (() => {
    const { tag, name } = decodeZelleQr(ZELLE_QR_URL);
    return Object.freeze({ qrUrl: ZELLE_QR_URL, tag, name });
  })();

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * THE MEMO OF AN INVOICE SENT BEFORE NUMBERING BEGAN: "FK" and the day of the clean, "FK Oct 27".
 * Invoices carry a number since migration `0033`, `FK-42`, and the memo of one that has a number is
 * that number (`invoiceMemo` in `document-numbers.ts`, ops #76 O2), so two invoices of one day no
 * longer share a memo; this is the day form, for an invoice sent before, and the transfer carries the
 * payer's name, so the name and the day are how the office matches its Zelle in the Chase app. A day
 * that is not `YYYY-MM-DD` throws.
 */
export function zelleMemo(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  const month = match ? MONTHS[Number(match[2]) - 1] : undefined;
  const date = match ? Number(match[3]) : 0;
  if (!month || date < 1 || date > 31) throw new Error(`not a calendar day: ${day}`);
  return `FK ${month} ${date}`;
}
